// @flow

/**
 * Displays a hero photo given a set of photo sources.
 *
 * There are 3 hero photo strategies:
 * 1. Photo sources are passed in (from HomepageHeroNodeRenderer). This is a short-term solution to allow graceful fallback to existing homepage hero placement.
 * 2. GraphQL query to retrieve photo sources.
 * 3. Default photo sources if all else fails.
 *
 * @author rbernard
 * @since Jan 2019
 */

import * as React from 'react';
import { Query } from 'react-apollo';
import { type PhotoInfo } from '@ta/media.common';
import {
  ResponsiveImage,
  type Source as ResponsiveImageSource,
  type ContainerDimensions,
} from '@ta/media.image';

import LocationThumbnail from './LocationThumbnail.gql';

type Props = {
  locationId?: number,
  photo?: PhotoInfo,
  backgroundPosition?: string,
  ssrContainerDimensions?: ContainerDimensions,
};

/**
 * The default homepage hero photo in case all else fails.
 * @type {{photoSizes: *[], backgroundPosition: string}}
 */
export const defaultHero = {
  photoSizes: [
    {
      url: '/img2/branding/homepage/cinque_terre_home_1280x250.jpg',
      width: 1280,
      height: 250,
    },
    {
      url: '/img2/branding/homepage/cinque_terre_home_2560x500.jpg',
      width: 2560,
      height: 500,
    },
  ],
  backgroundPosition: '50% bottom', // align the people with their beach chairs and umbrellas to the bottom edge
};

const showHero = (
  sources: Array<ResponsiveImageSource>,
  backgroundPosition?: string,
  ssrContainerDimensions?: ContainerDimensions,
) => (
  <ResponsiveImage
    sources={sources}
    backgroundPosition={backgroundPosition}
    ssrContainerDimensions={ssrContainerDimensions}
    width="100%"
    height="100%"
    notLazy
  />
);

const HeroPhoto = ({ locationId, photo, backgroundPosition, ssrContainerDimensions }: Props) => {
  if (photo && photo.photoSizes) {
    return showHero(photo.photoSizes, backgroundPosition, ssrContainerDimensions);
  }
  if (!locationId) {
    return showHero(defaultHero.photoSizes, defaultHero.backgroundPosition, ssrContainerDimensions);
  }
  /* istanbul ignore next */
  return (
    <Query query={LocationThumbnail} variables={{ locationId }}>
      {({ loading, data }) => {
        if (loading) {
          return null;
        }
        const photoSizes =
          data &&
          data.locations &&
          data.locations[0] &&
          data.locations[0].thumbnail &&
          data.locations[0].thumbnail.photoSizes;
        if (photoSizes) {
          /* $FlowFixGraphql (WP-1357) */
          return showHero(photoSizes);
        }
        return showHero(
          defaultHero.photoSizes,
          defaultHero.backgroundPosition,
          ssrContainerDimensions,
        );
      }}
    </Query>
  );
};

HeroPhoto.defaultProps = {
  locationId: undefined,
  photo: undefined,
  backgroundPosition: undefined,
  ssrContainerDimensions: undefined,
};

export default HeroPhoto;
